/**
 * AppsMarketplaceSection — the App Marketplace rendered as a section
 * inside MarketplacePage. No outer `<div className="marketplace-page">`
 * wrapper; the parent owns that. Use this when you want the apps tiles
 * to sit alongside / below the workflow tiles on the same page.
 *
 * For the standalone deep-link `/marketplace/apps`, AppsMarketplacePage
 * wraps this same component with its own page wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import { rocketOutline, openOutline, alertCircle, star, starOutline, addOutline } from 'ionicons/icons';

import { Apps } from '../../services/apps';
import ProjectsService from '../../services/projects';
import authService from '../../services/auth';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance, hoursOfRuntime } from '../../services/credits';
import WorkspaceCredentials from '../../services/workspaceCredentials';
import ClaudeCredentialModal from '../../components/integrations/ClaudeCredentialModal';
import OpenAICredentialModal from '../../components/integrations/OpenAICredentialModal';
import Field from '../../components/atoms/Field/Field';
import Dropdown from '../../components/atoms/Dropdown/Dropdown';
import AppPricingBadge, { resolveTier } from '../../components/billing/AppPricingBadge';
import { projectAppsMarketplacePath } from '../../utils/marketplacePaths';
import MktAlertModal, { MktAlertModalLink } from '../../components/molecules/MktAlertModal/MktAlertModal';
// Share the same Choose-a-Project + Create-new styles the workflow
// deploy wizard uses — so apps deploy flow looks identical.
import '../MarketplacePage/MarketplaceDeployWizardModal.css';
import '../MarketplacePage/MarketplaceDetailModal.css';
import './AppsMarketplacePage.css';

/**
 * @param {Object} props
 * @param {boolean} [props.showHeader=true]  Render the section's own h2 +
 *   subtitle. Pass false when the parent page already provides the
 *   heading (e.g. the standalone /marketplace/apps wrapper).
 * @param {boolean} [props.embedded=false]  When true (combined /marketplace
 *   page below workflows), show the top divider + extra spacing. Standalone
 *   /marketplace/apps and project-scoped routes leave this false.
 */
// Special-case labels for short / acronym categories so chips read
// nicely. Anything not in the map is title-cased.
const APPS_CATEGORY_LABELS = {
  ai: 'AI',
  ci: 'CI',
  db: 'DB',
};
function formatCategoryLabel(name) {
  if (!name) return '—';
  const lower = String(name).toLowerCase();
  if (APPS_CATEGORY_LABELS[lower]) return APPS_CATEGORY_LABELS[lower];
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const AppsMarketplaceSection = ({ showHeader = true, embedded = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Active category chip (null === "All"). Same model as the workflow
  // marketplace chip row above — single-select toggle.
  const [activeFacet, setActiveFacet] = useState(null);

  // Deploy modal state
  const [active, setActive] = useState(null);     // catalog tile (list-shape)
  const [activeDetail, setActiveDetail] = useState(null); // full detail (with myRating)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pickedProject, setPickedProject] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  // Rating UX state (mirrors workflow modal)
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  // Two-step deploy flow (mirrors the workflow detail → wizard
  // hand-off). Step 1 = info / ratings / cost / facts; step 2 = pick
  // a project + name the instance + Deploy. Project-scoped opens
  // (e.g. /projects/:id/marketplace) skip step 1's project picker but
  // still preserve the same visual two-stage flow.
  const [step, setStep] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // Credit balance for the cost banner in the deploy modal. null = not
  // yet fetched. Reloaded on every modal-open + when the global
  // `credit-changed` event fires (post-checkout return).
  const [creditBalanceMc, setCreditBalanceMc] = useState(null);
  // Drives the "Add credits to deploy" alert modal — opened when a
  // deploy is attempted without enough balance (front-end fast path or
  // backend rejection). See handleDeploy.
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  // Lowercased set of instance names already used in the target project
  // — drives real name-collision detection (the backend does NOT enforce
  // name uniqueness, so two same-named instances are otherwise allowed).
  const [existingNames, setExistingNames] = useState(() => new Set());

  // Agent provider — 'claude' (default) or 'codex'. Drives which BYOK
  // credential gate the deploy modal checks, and what the POST body
  // sends. State is reset to 'claude' on each modal open so picking
  // codex for one app doesn't sticky across the next deploy.
  const [pickedProvider, setPickedProvider] = useState('claude');

  // BYOK credential gate per provider. State drives the two deploy-modal
  // states (per provider):
  //   { has<X>: true,  mode, suffix } → State A (one-click deploy with a
  //                                       "using …abc123" affordance)
  //   { has<X>: false }              → State B (paste-token modal opens
  //                                       inline; Save → re-fetch → State A)
  // null means "not yet fetched" — we lazy-fetch on modal open + when the
  // backend 428s inline (race-safety: cred deleted between open + Deploy).
  const [claudeCred, setClaudeCred] = useState(null);
  const [openaiCred, setOpenaiCred] = useState(null);
  const [claudeModalOpen, setClaudeModalOpen] = useState(false);
  const [openaiModalOpen, setOpenaiModalOpen] = useState(false);
  const refreshClaudeCred = useCallback(async () => {
    const status = await WorkspaceCredentials.getClaudeStatus();
    setClaudeCred(status || { hasClaude: false });
    return status;
  }, []);
  const refreshOpenaiCred = useCallback(async () => {
    const status = await WorkspaceCredentials.getOpenAIStatus();
    setOpenaiCred(status || { hasOpenAI: false });
    return status;
  }, []);

  const { projectId: projectScopedId } = useParams();
  const navigate = useNavigate();
  const isAuthed = authService.isAuthenticated?.() ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.listCatalog();
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load apps catalog:', err);
        setError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the URL is project-scoped (/projects/:id/marketplace) we skip
  // the project-picker entirely — the user already chose. We still
  // fetch the project NAME so the modal can show which project the
  // instance lands in. Outside a project, we fetch the full list so
  // the user can pick.
  const ensureProjects = useCallback(async () => {
    if (projectScopedId) {
      // Already inside a project — pin to that id and resolve its name
      // for display. A single-project fetch would be cheaper but the
      // shared service only exposes a list call; the result is cached.
      if (pickedProject === projectScopedId && projects.length > 0) return;
      setProjectsLoading(true);
      try {
        const res = await ProjectsService.getProjects();
        const list = res?.projects || [];
        setProjects(list);
        setPickedProject(projectScopedId);
      } catch (err) {
        console.error('Failed to resolve project name:', err);
        setPickedProject(projectScopedId);
      } finally {
        setProjectsLoading(false);
      }
      return;
    }
    if (projects.length > 0 || projectsLoading) return;
    setProjectsLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      const list = res?.projects || [];
      setProjects(list);
      setPickedProject(list[0]?.projectId || list[0]?.id || '');
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [projects.length, projectsLoading, projectScopedId, pickedProject]);

  // Project name lookup for the "deploying to" line shown when the
  // picker is hidden (project-scoped route).
  const pickedProjectName = React.useMemo(() => {
    const p = projects.find((x) => (x.projectId || x.id) === pickedProject);
    return p?.name || '';
  }, [projects, pickedProject]);

  const openDeploy = useCallback(async (entry) => {
    if (!isAuthed) {
      const next = projectScopedId
        ? projectAppsMarketplacePath(projectScopedId)
        : '/marketplace/apps';
      navigate(`/signup?next=${encodeURIComponent(next)}`);
      return;
    }
    setActive(entry);
    setActiveDetail(entry); // optimistic — show list fields immediately
    setInstanceName('');
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
    // Project-scoped: pin the picked project immediately so the inline
    // instance-name + Deploy footer renders right away (no flash while
    // ensureProjects resolves the name async). The detail body + this
    // footer then show together in one modal — no Continue step.
    if (projectScopedId) setPickedProject(projectScopedId);
    ensureProjects();
    // Fetch credit balance for the cost banner. Non-fatal — if the API
    // is unreachable the banner just shows "balance unknown" without
    // blocking deploy.
    Credits.getBalance()
      .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
      .catch(() => setCreditBalanceMc(null));
    // Reset provider pick to Claude on every modal open — sticky picks
    // across apps would be surprising. The user re-selects explicitly
    // per deploy.
    setPickedProvider('claude');
    // Fetch BOTH provider credential statuses (BYOK gates). State A vs
    // State B branch per provider is driven by these; refresh on modal
    // open so a token added in another tab is reflected without a page
    // reload.
    refreshClaudeCred();
    refreshOpenaiCred();
    // Refetch detail so we have myRating + fresh aggregates the moment
    // the modal opens. Non-fatal if it fails — list fields are enough.
    try {
      const fresh = await Apps.getCatalogEntry(entry.appType);
      setActiveDetail(fresh);
    } catch (e) {
      console.warn('Failed to refresh catalog detail:', e.message);
    }
  }, [isAuthed, navigate, ensureProjects, refreshClaudeCred, refreshOpenaiCred]);

  // Listen for global credit-changed events so the banner stays fresh
  // if the user tops up in another tab while the modal is open.
  useEffect(() => {
    const handler = () => {
      if (!active) return;
      Credits.getBalance()
        .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
        .catch(() => {});
    };
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [active]);

  // Fetch the names already taken in the target project so the deploy
  // form can flag a collision before submit. Re-runs when the picked
  // project changes (project-scoped pins it immediately; global picks
  // it on step 2). Destroyed instances free their name for reuse.
  useEffect(() => {
    if (!active || !pickedProject) { setExistingNames(new Set()); return undefined; }
    let cancelled = false;
    Apps.listInstances({ projectId: pickedProject })
      .then((res) => {
        if (cancelled) return;
        // listApps returns `{ apps: [...] }` (NOT `items`); the server
        // already excludes destroyed rows, so everything returned counts
        // as a taken name.
        const list = res?.apps || res?.items || [];
        const names = new Set(
          list
            .map((it) => String(it.displayName || it.name || '').trim().toLowerCase())
            .filter(Boolean),
        );
        setExistingNames(names);
      })
      .catch(() => { if (!cancelled) setExistingNames(new Set()); });
    return () => { cancelled = true; };
  }, [active, pickedProject]);

  const closeDeploy = () => {
    if (deploying) return;
    setActive(null);
    setActiveDetail(null);
    setDeployError(null);
    setHoverRating(0);
    setStep(1);
    setShowCreateForm(false);
    setNewProjectName('');
  };

  // 0-project users: when they reach step 2, skip the dropdown and land
  // in the create form so they can name their first project immediately.
  useEffect(() => {
    if (!active || step !== 2 || projectScopedId || projectsLoading) return;
    if (projects.length === 0 && !pickedProject && !showCreateForm) {
      setShowCreateForm(true);
    }
  }, [active, step, projectScopedId, projectsLoading, projects.length, pickedProject, showCreateForm]);

  // Create + auto-pick a new project. Mirrors MarketplaceDeployWizardModal's
  // handleCreateAndPick.
  const handleCreateAndPick = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim() || creatingProject) return;
    setCreatingProject(true);
    try {
      const created = await ProjectsService.createProject({ name: newProjectName.trim() });
      // The API may return { project: {...} } or the row directly.
      const row = created?.project || created;
      if (row?.id || row?.projectId) {
        const id = row.id || row.projectId;
        setProjects((list) => [...list, row]);
        setPickedProject(id);
        setShowCreateForm(false);
        setNewProjectName('');
      }
    } catch (err) {
      console.error('Create project failed:', err);
      setDeployError(err.message || 'Could not create project');
    } finally {
      setCreatingProject(false);
    }
  };

  // Submit a rating. Backend recomputes the META aggregate atomically;
  // the response gives us the new avg + count without a follow-up GET.
  // Also patch the corresponding tile in `items` so the list view's
  // rating chip updates immediately without re-fetching the catalog.
  const submitRating = useCallback(async (n) => {
    if (!active?.appType || submittingRating) return;
    setSubmittingRating(true);
    try {
      const res = await Apps.rate(active.appType, { rating: n });
      const next = {
        ratingAvg: Number(res.ratingAvg) || 0,
        ratingCount: Number(res.ratingCount) || 0,
        ratingHistogram: res.ratingHistogram || activeDetail?.ratingHistogram,
      };
      setActiveDetail((prev) => prev ? {
        ...prev,
        ...next,
        myRating: { rating: n, review: '', ratedAt: new Date().toISOString() },
      } : prev);
      setItems((list) => list.map((it) => (
        it.appType === active.appType ? { ...it, ...next } : it
      )));
    } catch (e) {
      console.error('Rate failed:', e);
    } finally {
      setSubmittingRating(false);
    }
  }, [active, activeDetail, submittingRating]);

  const handleDeploy = async () => {
    if (!active || !pickedProject) return;
    // Block duplicate names in the same project (backend doesn't enforce).
    const wanted = (instanceName.trim() || active.displayName || '').trim().toLowerCase();
    if (wanted && existingNames.has(wanted)) return;
    // Fast path — if we already know the balance is short, pop the
    // top-up modal without a round-trip. When the balance hasn't
    // loaded yet (creditBalanceMc null) we skip this and let the
    // backend be the gate; the catch below routes its insufficient-
    // credits rejection to the SAME modal (never an inline banner).
    const tierInfo = resolveTier(active.pricing?.tier);
    const fiveMinCost = tierInfo.milliCentsPerMin * 5;
    if (creditBalanceMc !== null && creditBalanceMc < fiveMinCost) {
      setShowInsufficientCredits(true);
      return;
    }
    setDeploying(true);
    setDeployError(null);
    try {
      const res = await Apps.deploy({
        appType: active.appType,
        projectId: pickedProject,
        name: instanceName || undefined,
        // Only forward when picked is non-default — keeps the wire shape
        // unchanged for Claude deploys (matches every CLI/MCP request).
        provider: pickedProvider !== 'claude' ? pickedProvider : undefined,
      });
      const instanceId = res?.instanceId;
      if (instanceId) {
        navigate(`/projects/${pickedProject}/apps/${instanceId}`);
      } else {
        closeDeploy();
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      const msg = err.message || 'Deploy failed';
      const code = err?.body?.code || err?.code;
      // Insufficient-credits rejection → the dedicated "Add credits to
      // deploy" modal, NOT an inline red banner. Match the backend's
      // wording ("Insufficient credits … Need at least … runway …").
      if (/insufficient credit|need at least|top up|runway/i.test(msg)) {
        setShowInsufficientCredits(true);
      } else if (code === 'CLAUDE_CREDENTIAL_REQUIRED') {
        // Race: cred existed at modal-open, deleted before Deploy click.
        // Flip into State B by clearing the cached status + opening the
        // paste-token modal. Same code-only contract the CLI uses; never
        // parse the human message.
        setClaudeCred({ hasClaude: false });
        setClaudeModalOpen(true);
      } else if (code === 'OPENAI_CREDENTIAL_REQUIRED') {
        // Codex counterpart of the Claude race above. Provider was
        // picked=codex but the OpenAI cred vanished between open and
        // Deploy (or wasn't yet present).
        setOpenaiCred({ hasOpenAI: false });
        setOpenaiModalOpen(true);
      } else {
        setDeployError(msg);
      }
      setDeploying(false);
    }
  };

  // Render nothing (not even the section heading) while loading or
  // when the catalog endpoint is unreachable. Local dev that hasn't
  // restarted devup.sh after pulling the catalog code will 404 on
  // GET /apps/catalog — better to hide the whole section than show a
  // scary "Not Found" panel beneath the workflow marketplace.
  if (loading || error || items.length === 0) return null;

  // Build the chip row from the union of catalog categories. Sort by
  // count desc → ties by label asc, so the most-represented category
  // sits next to the "All" pill.
  const facets = (() => {
    const counts = new Map();
    for (const it of items) {
      const c = String(it.category || '').trim().toLowerCase();
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count, label: formatCategoryLabel(name) }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  })();

  // Visible items: apply category filter. Null facet === show all.
  const visibleItems = activeFacet
    ? items.filter((it) => String(it.category || '').toLowerCase() === activeFacet)
    : items;

  // Effective instance name = what the user typed, else the default the
  // backend would use (catalog displayName). Collision = that name (case-
  // insensitive) already exists in the project. `active?.` keeps this
  // safe to evaluate even when no modal is open.
  const effectiveName = (instanceName.trim() || active?.displayName || '').trim().toLowerCase();
  const nameTaken = !!effectiveName && existingNames.has(effectiveName);

  return (
    <>
      <section
        className={`marketplace-section apps-section${embedded ? ' apps-section--embedded' : ''}`}
      >
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}

        {/* Category chip row — mirrors the Workflow Marketplace chips
            (same `marketplace-chips*` classes so the styling rides
            along). Hidden when there's only one category — single-axis
            filter would be a no-op. */}
        {facets.length > 1 && (
          <div className="marketplace-chips-wrap">
            <nav className="marketplace-chips" aria-label="Filter apps">
              <button
                type="button"
                className={`marketplace-chip${activeFacet === null ? ' is-active' : ''}`}
                onClick={() => setActiveFacet(null)}
                aria-pressed={activeFacet === null}
              >
                All
                <span className="marketplace-chip-count">{items.length}</span>
              </button>
              {facets.map((facet) => {
                const isActive = activeFacet === facet.name;
                return (
                  <button
                    key={facet.name}
                    type="button"
                    className={`marketplace-chip${isActive ? ' is-active' : ''}`}
                    onClick={() => setActiveFacet(isActive ? null : facet.name)}
                    aria-pressed={isActive}
                  >
                    {facet.label}
                    <span className="marketplace-chip-count">{facet.count}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        <ol className="marketplace-grid">
          {visibleItems.map((entry, idx) => (
            <li key={entry.appType} className="marketplace-card-li">
              <button
                type="button"
                className="marketplace-card"
                onClick={() => openDeploy(entry)}
                aria-label={`Deploy ${entry.displayName}`}
              >
                <span className="marketplace-rank">{idx + 1}</span>
                <span className="marketplace-card-icon-wrap">
                  {entry.icon ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={entry.icon}
                      alt=""
                      className="marketplace-card-icon"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="marketplace-card-icon marketplace-card-icon--fallback">
                      {(entry.displayName || entry.appType).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="marketplace-card-body">
                  <span className="marketplace-card-name">
                    {entry.displayName}
                    <span className="apps-card-version">v{entry.appVersion}</span>
                  </span>
                  <span className="marketplace-card-tagline">{entry.tagline}</span>
                  <span className="marketplace-card-author">
                    By Zibby
                    <span className="marketplace-card-author-badge">Official</span>
                    {Number(entry.ratingCount) > 0 && (
                      <span
                        className="marketplace-card-rating"
                        aria-label={`${Number(entry.ratingAvg).toFixed(1)} stars, ${entry.ratingCount} rating${entry.ratingCount === 1 ? '' : 's'}`}
                      >
                        <IonIcon icon={star} aria-hidden="true" />
                        <span className="marketplace-card-rating-value">{Number(entry.ratingAvg).toFixed(1)}</span>
                        <span className="marketplace-card-rating-count">({entry.ratingCount})</span>
                      </span>
                    )}
                    {/* Pricing tier pill — fed by `pricing.tier` from the
                        catalog row (Agent C). Falls back to 'standard'
                        when missing so legacy catalog entries still
                        render a badge instead of nothing. */}
                    <AppPricingBadge tier={entry.pricing?.tier} className="apps-card-pricing" />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </section>

      {active && createPortal(
        <div className="mkt-modal-overlay" onClick={closeDeploy} role="presentation">
          <div
            className="mkt-modal"
            role="dialog"
            aria-label={`Deploy ${active.displayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mkt-modal-close"
              onClick={closeDeploy}
              aria-label="Close"
              disabled={deploying}
            >×</button>

            <div className="mkt-modal-body">
              {/* Reuse the workflow-detail-modal header shape (icon
                  centered on top, title centered below) so the apps
                  modal looks like a sibling of the workflow modal. */}
              <header className="mkt-modal-header">
                {active.icon ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={active.icon} alt="" className="mkt-modal-icon" />
                ) : (
                  <div className="mkt-modal-icon mkt-modal-icon--fallback">
                    {(active.displayName || active.appType).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2 className="mkt-modal-title">
                  {(step === 2 && !projectScopedId) ? `Deploy ${active.displayName}` : active.displayName}
                </h2>
                {(step === 1 || projectScopedId) && (
                  <p className="mkt-modal-author">
                    By Zibby
                    <span className="mkt-modal-author-badge">Official</span>
                  </p>
                )}
                <p className="mkt-modal-tagline">
                  {(step === 2 && !projectScopedId)
                    ? "Apps live inside projects. Pick an existing project — or create a new one — and we'll deploy this instance into it."
                    : active.tagline}
                </p>

                {/* Stats row — only on step 1 (detail). Step 2 (wizard)
                    is minimal: just icon + title + tagline + choose-a-
                    project, matching the workflow deploy wizard. */}
                {(step === 1 || projectScopedId) && (() => {
                  const detail = activeDetail || active;
                  const rating = Number(detail.ratingAvg) || 0;
                  const myRatingValue = Number(detail.myRating?.rating) || 0;
                  const canRate = isAuthed;
                  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
                  return (
                    <div className="mkt-modal-stats" aria-label="Stats">
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">
                          <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                          <span
                            className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                            role={canRate ? 'radiogroup' : undefined}
                            aria-label={canRate ? 'Rate this app' : undefined}
                            onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                          >
                            {[1, 2, 3, 4, 5].map((n) => {
                              const filled = n <= starsFilled;
                              const StarEl = canRate ? 'button' : 'span';
                              const extra = canRate ? {
                                type: 'button',
                                role: 'radio',
                                'aria-checked': n === myRatingValue,
                                'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                                disabled: submittingRating,
                                onMouseEnter: () => setHoverRating(n),
                                onFocus: () => setHoverRating(n),
                                onBlur: () => setHoverRating(0),
                                onClick: () => submitRating(n),
                              } : {};
                              return (
                                <StarEl key={n} className="mkt-modal-star" {...extra}>
                                  <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                                </StarEl>
                              );
                            })}
                          </span>
                        </div>
                        <div className="mkt-modal-stat-label">
                          {(Number(detail.ratingCount) || 0)} RATING{Number(detail.ratingCount) === 1 ? '' : 'S'}
                        </div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value mkt-modal-stat-category">
                          {active.category || '—'}
                        </div>
                        <div className="mkt-modal-stat-label">Category</div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">v{active.appVersion}</div>
                        <div className="mkt-modal-stat-label">Version</div>
                      </div>
                    </div>
                  );
                })()}
              </header>

              {/* ── STEP 1 — detail/info view. Cost banner + heads-up
                  + facts. Footer is a single "Continue" CTA that flips
                  to step 2 (the actual deploy wizard). */}
              {(step === 1 || projectScopedId) && (() => {
                const tierInfo = resolveTier(active.pricing?.tier);
                const ratePerMin = tierInfo.milliCentsPerMin;
                const balance = creditBalanceMc;
                const hrs = balance !== null ? hoursOfRuntime(ratePerMin, balance) : null;
                const balanceStr = balance !== null ? formatBalance(balance) : '—';
                const hrsStr = hrs !== null
                  ? (hrs >= 1
                      ? `≈ ${Math.floor(hrs).toLocaleString()} hours of runtime`
                      : (hrs > 0 ? `≈ ${Math.round(hrs * 60)} min of runtime` : 'insufficient runtime'))
                  : '';
                return (
                  <>
                    <div className="apps-modal-cost-banner">
                      <strong>This app costs {tierInfo.hourly} while running.</strong>
                      {' '}Your balance: <strong>{balanceStr}</strong>
                      {hrsStr ? ` (${hrsStr})` : ''}
                    </div>

                    {active.ui?.setupHint && (
                      <p className="apps-modal-hint">
                        <strong>Heads up:</strong> {active.ui.setupHint}
                      </p>
                    )}

                    <ul className="apps-modal-facts">
                      <li><strong>Public URL</strong> <span>https://<em>{'<id>'}</em>.apps.zibby.dev</span></li>
                      <li><strong>Resources</strong> <span>{(active.resources.cpu / 1024).toFixed(2)} vCPU · {(active.resources.memory / 1024).toFixed(2)} GB RAM</span></li>
                      {active.homepage && (
                        <li>
                          <strong>Homepage</strong>
                          <span>
                            <a
                              href={active.homepage}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="apps-modal-homepage-link"
                            >
                              <IonIcon icon={openOutline} aria-hidden="true" />
                              {active.homepage.replace(/^https?:\/\//, '')}
                            </a>
                          </span>
                        </li>
                      )}
                    </ul>
                  </>
                );
              })()}

              {/* ── STEP 2 — choose a project. Same shape + classes as
                  the workflow MarketplaceDeployWizardModal. Hidden
                  when the URL is already project-scoped (we know which
                  project this app gets deployed into). */}
              {step === 2 && !projectScopedId && (
                <section className="mkt-modal-section">
                  <h3 className="mkt-modal-section-title">Choose a project</h3>

                  {projectsLoading && projects.length === 0 ? (
                    <div className="mkt-wizard-loading">
                      <IonSpinner name="crescent" />
                      <span>Loading your projects…</span>
                    </div>
                  ) : null}

                  {projects.length > 0 && !showCreateForm && (
                    <Dropdown
                      className="mkt-wizard-project-dropdown"
                      options={[
                        { key: '', label: 'Select a project…' },
                        ...projects.map((p) => ({ key: p.projectId || p.id, label: p.name })),
                      ]}
                      value={pickedProject || ''}
                      onChange={(v) => setPickedProject(v || '')}
                    />
                  )}

                  {!showCreateForm ? (
                    <button
                      type="button"
                      className="mkt-wizard-create-toggle"
                      onClick={() => {
                        setShowCreateForm(true);
                        setPickedProject('');
                      }}
                    >
                      <IonIcon icon={addOutline} aria-hidden="true" />
                      <span>Create a new project</span>
                    </button>
                  ) : (
                    <form onSubmit={handleCreateAndPick} className="mkt-wizard-create-form">
                      <label className="mkt-wizard-create-label" htmlFor="apps-wizard-new-project">
                        New project name
                      </label>
                      <div className="mkt-wizard-create-row">
                        <input
                          id="apps-wizard-new-project"
                          type="text"
                          className="mkt-wizard-create-input"
                          value={newProjectName}
                          onChange={(e) => setNewProjectName(e.target.value)}
                          placeholder="my-project"
                          autoFocus
                          disabled={creatingProject}
                          autoComplete="off"
                        />
                        <button
                          type="submit"
                          className="mkt-wizard-create-submit"
                          disabled={creatingProject || !newProjectName.trim()}
                        >
                          {creatingProject ? 'Creating…' : 'Create'}
                        </button>
                        {projects.length > 0 && (
                          <button
                            type="button"
                            className="mkt-wizard-create-cancel"
                            onClick={() => {
                              setShowCreateForm(false);
                              setNewProjectName('');
                            }}
                            disabled={creatingProject}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  )}
                </section>
              )}

              {deployError && (
                <div className="apps-modal-error">
                  <IonIcon icon={alertCircle} aria-hidden="true" /> {deployError}
                </div>
              )}
            </div>

            {/* ── Footer transitions with `step`. Step 1 = single
                "Continue" CTA. Step 2 = instance-name field +
                Deploy button + Back link. Same `mkt-modal-name-*`
                classes as the workflow wizard footer. */}
            <footer className="mkt-modal-footer apps-modal-footer">
              {step === 1 && !projectScopedId && (
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  onClick={() => setStep(2)}
                >
                  Continue
                </button>
              )}

              {step === 2 && !pickedProject && (
                /* No project picked yet → full-width disabled Deploy
                   placeholder. Identical shape to MarketplaceDeployWizardModal's
                   pre-pick footer. */
                <button
                  type="button"
                  className="mkt-modal-deploy mkt-modal-deploy--full"
                  disabled
                  aria-disabled="true"
                  title="Pick or create a project first"
                >
                  Deploy
                </button>
              )}

              {(step === 2 || projectScopedId) && pickedProject && (
                /* Project picked → reveal the provider picker + instance-
                   name field + final Deploy button. Same shape as the
                   workflow wizard once it transitions into the v3 footer.
                   Project-scoped shows this inline beneath the detail
                   (no Continue step) — one modal, like the workflow
                   deploy modal. */
                <>
                  {/* Provider radio — Claude (default) vs Codex. Sits
                      ABOVE the name field so the user sees the choice
                      before naming. We don't auto-detect from the app
                      type because users might want to try Codex against
                      any catalog tile. Default Claude preserves existing
                      flow for every user who doesn't touch the radio. */}
                  <div
                    role="radiogroup"
                    aria-label="Agent provider"
                    style={{
                      display: 'flex', gap: 8, marginBottom: 12,
                    }}
                  >
                    {[
                      { value: 'claude', label: 'Claude (Sonnet)', sub: 'Anthropic' },
                      { value: 'codex',  label: 'Codex (GPT-5)',  sub: 'OpenAI' },
                    ].map((opt) => {
                      const isActive = pickedProvider === opt.value;
                      return (
                        <label
                          key={opt.value}
                          style={{
                            flex: 1, cursor: deploying ? 'not-allowed' : 'pointer',
                            padding: 10, borderRadius: 6,
                            border: `1px solid ${isActive ? 'var(--ion-color-primary)' : 'rgba(255,255,255,0.15)'}`,
                            background: isActive ? 'rgba(99,102,241,0.12)' : 'transparent',
                          }}
                        >
                          <input
                            type="radio"
                            name="apps-deploy-provider"
                            value={opt.value}
                            checked={isActive}
                            onChange={() => setPickedProvider(opt.value)}
                            disabled={deploying}
                            style={{ marginRight: 8 }}
                          />
                          <span style={{ fontWeight: 600 }}>{opt.label}</span>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                            {opt.sub}
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="mkt-modal-name-label-row">
                    <label className="mkt-modal-name-label" htmlFor="apps-deploy-name-input">
                      Instance name
                    </label>
                    <p
                      className={`mkt-modal-name-help ${nameTaken ? '' : 'is-valid'}`}
                      style={nameTaken ? { color: '#ef4444' } : undefined}
                      aria-live="polite"
                    >
                      {nameTaken
                        ? 'Name taken — pick another'
                        : (pickedProjectName ? `Available — will deploy to ${pickedProjectName}` : '')}
                    </p>
                  </div>
                  <div className={`mkt-modal-name-row ${nameTaken ? '' : 'is-valid'}`}>
                    <Field
                      id="apps-deploy-name-input"
                      type="text"
                      className="mkt-modal-name-input"
                      value={instanceName}
                      onChange={(e) => setInstanceName(e.target.value.slice(0, 80))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !deploying) {
                          e.preventDefault();
                          handleDeploy();
                        }
                      }}
                      placeholder={active.displayName}
                      autoComplete="off"
                      spellCheck="false"
                      disabled={deploying}
                      status={nameTaken ? 'error' : 'ok'}
                    />
                    {(() => {
                      const tierInfo = resolveTier(active.pricing?.tier);
                      const fiveMinCost = tierInfo.milliCentsPerMin * 5;
                      const insufficient = creditBalanceMc !== null
                        && creditBalanceMc < fiveMinCost;
                      // BYOK gate — driven by the picked provider. Two
                      // states share this wrapper, per provider:
                      //   - hasCred: ordinary Deploy button (State A);
                      //     below the row we show "Will use … <suffix>".
                      //   - !hasCred: swap Deploy for a "Connect <provider>"
                      //     button that opens that provider's paste-token
                      //     modal; on its success we re-fetch the status
                      //     and the user clicks Deploy again. Null = not
                      //     yet fetched (Deploy lets the backend gate;
                      //     handleDeploy catches *_CREDENTIAL_REQUIRED
                      //     inline and flips into State B).
                      const isCodex = pickedProvider === 'codex';
                      const cred = isCodex ? openaiCred : claudeCred;
                      const hasCred = isCodex ? cred?.hasOpenAI : cred?.hasClaude;
                      const noCred = cred !== null && hasCred === false;
                      const connectLabel = isCodex ? 'Connect OpenAI' : 'Connect Claude';
                      const onConnect = () => {
                        if (isCodex) setOpenaiModalOpen(true);
                        else setClaudeModalOpen(true);
                      };
                      return (
                        <span className="mkt-modal-deploy-split">
                          {noCred ? (
                            <button
                              type="button"
                              className="mkt-modal-deploy"
                              disabled={deploying || nameTaken}
                              onClick={onConnect}
                              title={`Connect ${isCodex ? 'OpenAI' : 'Claude'} credential to enable deploy`}
                            >
                              <IonIcon icon={rocketOutline} aria-hidden="true" />
                              {connectLabel}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="mkt-modal-deploy"
                              disabled={deploying || nameTaken}
                              onClick={handleDeploy}
                              title={nameTaken ? 'Name taken — pick another' : (insufficient ? 'Add credits to deploy' : undefined)}
                            >
                              <IonIcon icon={rocketOutline} aria-hidden="true" />
                              {deploying ? 'Deploying…' : 'Deploy'}
                            </button>
                          )}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Cred affordance for the picked provider: suffix the
                      deploy will use (State A) or a hint to connect
                      (State B). Lives BELOW the name row so the deploy
                      button stays anchored at the same y-position across
                      states. */}
                  {(() => {
                    const isCodex = pickedProvider === 'codex';
                    const cred = isCodex ? openaiCred : claudeCred;
                    const hasCred = isCodex ? cred?.hasOpenAI : cred?.hasClaude;
                    if (cred && hasCred) {
                      return (
                        <p
                          className="mkt-modal-name-help is-valid"
                          style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}
                          aria-live="polite"
                        >
                          {isCodex ? (
                            <>Will use your OpenAI API key ({cred.suffix || '***'})</>
                          ) : (
                            <>Will use your Claude{' '}
                              {cred.mode === 'oauth' ? 'subscription' : 'API key'}
                              {' '}({cred.suffix || '***'})
                            </>
                          )}
                        </p>
                      );
                    }
                    if (cred && hasCred === false) {
                      return (
                        <p
                          className="mkt-modal-name-help"
                          style={{ marginTop: 8, fontSize: 12, color: '#f59e0b' }}
                          aria-live="polite"
                        >
                          {isCodex
                            ? 'No OpenAI API key connected. Click Connect OpenAI above to paste one — required for Codex deploy.'
                            : 'No Claude credential connected. Click Connect Claude above to paste one — required for deploy.'}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </footer>
          </div>
        </div>,
        document.body
      )}

      <ClaudeCredentialModal
        isOpen={claudeModalOpen}
        onClose={() => setClaudeModalOpen(false)}
        defaultKind={claudeCred?.mode || 'oauth'}
        onConnected={async ({ mode, suffix }) => {
          // Cred is saved server-side. Optimistically flip state to A so
          // the deploy button enables immediately, then re-fetch for the
          // canonical suffix. The user clicks Deploy again — this avoids
          // any auto-deploy behavior that could surprise.
          setClaudeCred({ hasClaude: true, mode, suffix });
          refreshClaudeCred();
        }}
      />

      <OpenAICredentialModal
        isOpen={openaiModalOpen}
        onClose={() => setOpenaiModalOpen(false)}
        onConnected={async ({ suffix }) => {
          // Same optimistic-flip pattern as the Claude modal above —
          // user re-clicks Deploy when they're ready.
          setOpenaiCred({ hasOpenAI: true, mode: 'api', suffix });
          refreshOpenaiCred();
        }}
      />

      <MktAlertModal
        open={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        title="Add credits to deploy"
        ariaLabel="Insufficient credits"
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        onPrimary={() => closeDeploy()}
        secondaryLabel="Cancel"
      >
        {active && (() => {
          const tierInfo = resolveTier(active.pricing?.tier);
          const fiveMinCost = tierInfo.milliCentsPerMin * 5;
          const balanceStr = creditBalanceMc !== null
            ? formatBalance(creditBalanceMc, { precision: 'auto' })
            : '—';
          const minStr = formatBalance(fiveMinCost, { precision: 'auto' });
          return (
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
              Deploying requires at least <strong style={{ color: '#fff' }}>5 minutes</strong> of
              runtime at this app&apos;s rate ({tierInfo.hourly}).
              Your balance is <strong style={{ color: '#fff' }}>{balanceStr}</strong>
              {' '}(minimum <strong style={{ color: '#fff' }}>{minStr}</strong>).
              {' '}Add credits on the{' '}
              <MktAlertModalLink
                to="/billing"
                onClick={() => {
                  setShowInsufficientCredits(false);
                  closeDeploy();
                }}
              >
                billing page
              </MktAlertModalLink>
              {' '}to continue.
            </p>
          );
        })()}
      </MktAlertModal>
    </>
  );
};

export default AppsMarketplaceSection;
